/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalIcon } from "lucide-react";
import { format } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";

export default function TestDriveDialog({ car, trigger, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [date, setDate] = useState();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a preferred date.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        type: "test_drive",
        name: form.name,
        email: form.email,
        phone: form.phone,
        car_id: car?.id,
        preferred_date: date.toISOString().slice(0, 10),
        message: form.message,
      });
      toast.success("Test drive request received! We'll be in touch soon.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", message: "" });
      setDate(undefined);
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="test-drive-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-primary">
            Schedule a Test Drive
          </DialogTitle>
          <DialogDescription>
            {car ? `${car.year} ${car.make} ${car.model}` : "Tell us when you'd like to stop by."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="td-name">Full Name</Label>
              <Input
                id="td-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="test-drive-name-input"
              />
            </div>
            <div>
              <Label htmlFor="td-phone">Phone</Label>
              <Input
                id="td-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="test-drive-phone-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="td-email">Email</Label>
            <Input
              id="td-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="test-drive-email-input"
            />
          </div>
          <div>
            <Label>Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left font-normal mt-1"
                  data-testid="test-drive-date-picker"
                >
                  <CalIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground mt-1">We're closed on Sundays.</p>
          </div>
          <div>
            <Label htmlFor="td-message">Notes (optional)</Label>
            <Textarea
              id="td-message"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="test-drive-message-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="test-drive-submit"
            >
              {submitting ? "Submitting…" : "Request Test Drive"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
